import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useAppDispatch } from '@/redux/Store';
import { fetchMessageThreads } from '@/redux/thunks/messagesThunks';
import { useSocket } from '@/contexts/SocketContext';
import ConversationsList from './components/ConversationsList';
import ChatArea from './components/ChatArea';
import './page.scss';

const MessagesPage: React.FC = () => {
  const dispatch = useAppDispatch();
  const { socket } = useSocket();
  const [searchParams, setSearchParams] = useSearchParams();
  const [selectedThreadId, setSelectedThreadId] = useState<string | null>(null);
  const [isMobileView, setIsMobileView] = useState(false);
  const [showChat, setShowChat] = useState(false); // For mobile navigation

  // Check if mobile view
  useEffect(() => {
    const checkMobileView = () => {
      setIsMobileView(window.innerWidth < 1024);
    };

    checkMobileView();
    window.addEventListener('resize', checkMobileView);
    return () => window.removeEventListener('resize', checkMobileView);
  }, []);


  // Fetch message threads on mount
  useEffect(() => {
    dispatch(fetchMessageThreads());
  }, [dispatch]);

  // Auto-mark messages as read while the user is viewing the thread.
  // State updates (lastMessage / unreadCount / activeThread.messages) are
  // owned by AppBar's global message-received handler — we only need the
  // read-receipt side effect here.
  useEffect(() => {
    if (!socket) return;

    const handleGlobalMessageReceived = (message: any) => {
      if (message.threadId === selectedThreadId && message.id) {
        // Small delay to ensure the message is saved in DB first
        setTimeout(() => {
          socket.emit('message-read', {
            threadId: message.threadId,
            messageId: message.id,
          });
        }, 100);
      }
    };

    socket.on('message-received', handleGlobalMessageReceived);

    return () => {
      socket.off('message-received', handleGlobalMessageReceived);
    };
  }, [socket, selectedThreadId]);

  // Handle thread query parameter
  useEffect(() => {
    const threadParam = searchParams.get('thread');
    if (threadParam) {
      setSelectedThreadId(threadParam);
      if (isMobileView) {
        setShowChat(true);
      }
      // Clear the query param after reading it
      setSearchParams({});
    }
  }, [searchParams, isMobileView, setSearchParams]);

  const handleSelectThread = (threadId: string) => {
    setSelectedThreadId(threadId);
    if (isMobileView) {
      setShowChat(true);
    }
  };

  const handleBackToList = () => {
    setShowChat(false);
    setSelectedThreadId(null);
  };

  // Always clear the selected thread on delete so the desktop chat panel
  // unmounts instead of letting the user keep typing into a thread they
  // just removed. Mobile also flips back to the conversation list.
  const handleThreadDeleted = () => {
    setSelectedThreadId(null);
    if (isMobileView) setShowChat(false);
  };

  // On desktop, show both panels
  // On mobile, show either list or chat based on showChat state
  const showConversationsList = !isMobileView || !showChat;
  const showChatArea = !isMobileView || showChat;

  return (
    <div className="messages-page">
      <div className="messages-container">
        {showConversationsList && (
          <div className="conversations-panel">
            <ConversationsList
              selectedThreadId={selectedThreadId}
              onSelectThread={handleSelectThread}
            />
          </div>
        )}

        {showChatArea && (
          <div className="chat-panel">
            <ChatArea
              threadId={selectedThreadId}
              onBack={isMobileView ? handleBackToList : undefined}
              onThreadDeleted={handleThreadDeleted}
            />
          </div>
        )}
      </div>
    </div>
  );
};

export default MessagesPage;
